import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  REDEMPTION_STATUSES,
  REDEMPTION_STATUS_LABELS,
  type AdminRedemption,
  type RedemptionStatus,
} from '@/lib/api';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_authed/redemptions/')({
  component: RedemptionsListPage,
});

// Page-size for the redemptions table. We keep it bounded server-side at
// 500; 50 is a comfortable client-side scroll window and matches the
// pagination buttons that lazily load more.
const PAGE_SIZE = 50;

interface Filters {
  status: RedemptionStatus | '';
  businessId: string;
  locationId: string;
  issuedFrom: string;
  issuedTo: string;
}

const EMPTY_FILTERS: Filters = {
  status: '',
  businessId: '',
  locationId: '',
  issuedFrom: '',
  issuedTo: '',
};

function RedemptionsListPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);

  const businessesQ = useQuery({
    queryKey: ['businesses'],
    queryFn: api.listBusinesses,
  });

  // Server params drop empties and convert date inputs to ISO datetimes.
  // We send issuedFrom as the start of the chosen day in the browser's
  // local zone (good enough for admin reporting — Cyprus is UTC+2/+3).
  const apiFilters = useMemo(
    () => ({
      status: filters.status || undefined,
      businessId: filters.businessId || undefined,
      locationId: filters.locationId || undefined,
      issuedFrom: filters.issuedFrom
        ? new Date(`${filters.issuedFrom}T00:00:00`).toISOString()
        : undefined,
      issuedTo: filters.issuedTo
        ? new Date(`${filters.issuedTo}T23:59:59.999`).toISOString()
        : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [filters, page],
  );

  const listQ = useQuery({
    queryKey: ['redemptions', apiFilters],
    queryFn: () => api.listRedemptions(apiFilters),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-h1">Redemptions</h1>
        <p className="mt-1 text-body text-slate-gray">
          Live view of perk redemptions across all routes. Use this to spot
          businesses that haven't been honouring perks, top up the optional
          bill amount after a partner reports it, or audit a specific token.
        </p>
      </header>

      {listQ.data ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {REDEMPTION_STATUSES.map((s) => {
            const summary = listQ.data.summary[s];
            return (
              <div key={s} className="card">
                <p className="text-caption uppercase tracking-wider text-slate-gray">
                  {REDEMPTION_STATUS_LABELS[s]}
                </p>
                <p className="mt-1 text-h1 text-dark-navy">{summary.count}</p>
                {summary.totalBillSum ? (
                  <p className="mt-1 text-caption text-slate-gray">
                    € {formatBill(summary.totalBillSum)} total bills
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <FiltersBar
        filters={filters}
        businesses={businessesQ.data?.businesses ?? []}
        onChange={(next) => {
          setPage(0);
          setFilters(next);
        }}
      />

      <section>
        {listQ.isLoading ? (
          <div className="card text-slate-gray">Loading redemptions…</div>
        ) : listQ.data && listQ.data.redemptions.length > 0 ? (
          <RedemptionsTable rows={listQ.data.redemptions} />
        ) : (
          <div className="card text-slate-gray">
            No redemptions match these filters yet.
          </div>
        )}

        {listQ.data ? (
          <div className="mt-4 flex items-center justify-between text-caption text-slate-gray">
            <span>
              Showing {page * PAGE_SIZE + 1}-
              {Math.min(listQ.data.total, page * PAGE_SIZE + listQ.data.redemptions.length)} of{' '}
              {listQ.data.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="btn-ghost"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Prev
              </button>
              <button
                className="btn-ghost"
                disabled={(page + 1) * PAGE_SIZE >= listQ.data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function FiltersBar({
  filters,
  businesses,
  onChange,
}: {
  filters: Filters;
  businesses: { id: string; name: string }[];
  onChange: (next: Filters) => void;
}) {
  const reset = () => onChange(EMPTY_FILTERS);

  return (
    <form
      className="card grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
      onSubmit={(e) => e.preventDefault()}
    >
      <div>
        <label className="label">Status</label>
        <select
          className="input"
          value={filters.status}
          onChange={(e) =>
            onChange({ ...filters, status: e.target.value as RedemptionStatus | '' })
          }
        >
          <option value="">All</option>
          {REDEMPTION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {REDEMPTION_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="label">Business</label>
        <select
          className="input"
          value={filters.businessId}
          onChange={(e) => onChange({ ...filters, businessId: e.target.value })}
        >
          <option value="">All</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="label">Issued from</label>
        <input
          type="date"
          className="input"
          value={filters.issuedFrom}
          onChange={(e) => onChange({ ...filters, issuedFrom: e.target.value })}
        />
      </div>
      <div>
        <label className="label">Issued to</label>
        <input
          type="date"
          className="input"
          value={filters.issuedTo}
          onChange={(e) => onChange({ ...filters, issuedTo: e.target.value })}
        />
      </div>
      <div className="flex items-end">
        <button type="button" className="btn-ghost" onClick={reset}>
          Reset filters
        </button>
      </div>
    </form>
  );
}

function RedemptionsTable({ rows }: { rows: AdminRedemption[] }) {
  const queryClient = useQueryClient();

  const updateBill = useMutation({
    mutationFn: ({ id, totalBill }: { id: string; totalBill: number | null }) =>
      api.updateRedemption(id, { totalBill }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redemptions'] });
    },
  });

  return (
    <div className="overflow-x-auto rounded-card border border-neutral-gray bg-soft-white">
      <table className="w-full text-left text-body">
        <thead className="bg-light-gray text-caption uppercase text-slate-gray">
          <tr>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Issued</th>
            <th className="px-4 py-2">Perk</th>
            <th className="px-4 py-2">Location</th>
            <th className="px-4 py-2">Business</th>
            <th className="px-4 py-2">Code</th>
            <th className="px-4 py-2 text-right">Total bill (€)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-neutral-gray/60 align-top">
              <td className="px-4 py-2">
                <StatusPill status={r.status} expiresAt={r.expiresAt} />
              </td>
              <td className="px-4 py-2">
                <div className="text-caption text-dark-navy">{formatTime(r.issuedAt)}</div>
                {r.redeemedAt ? (
                  <div className="text-caption text-success">
                    ↳ {formatTime(r.redeemedAt)}
                  </div>
                ) : (
                  <div className="text-caption text-slate-gray">
                    expires {formatTime(r.expiresAt)}
                  </div>
                )}
              </td>
              <td className="px-4 py-2 text-dark-navy">
                {r.perk ? r.perk.title.en : <span className="text-slate-gray/60">—</span>}
              </td>
              <td className="px-4 py-2 text-dark-navy">
                {r.location?.title?.en ?? <span className="text-slate-gray/60">—</span>}
              </td>
              <td className="px-4 py-2 text-dark-navy">
                {r.business?.name ?? <span className="text-slate-gray/60">—</span>}
              </td>
              <td className="px-4 py-2 font-mono text-caption text-dark-navy">
                {r.humanCode}
              </td>
              <td className="px-4 py-2 text-right">
                <BillEditor
                  redemption={r}
                  onSave={(totalBill) =>
                    updateBill.mutateAsync({ id: r.id, totalBill })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({
  status,
  expiresAt,
}: {
  status: RedemptionStatus;
  expiresAt: string;
}) {
  // Server hasn't run a sweep job yet — a row with status='issued' and
  // expiresAt in the past is effectively expired. Display the effective
  // state to avoid the admin chasing a "phantom" issued perk.
  const effective: RedemptionStatus =
    status === 'issued' && new Date(expiresAt) < new Date() ? 'expired' : status;
  return (
    <span
      className={cn(
        'inline-block rounded-full px-2 py-0.5 text-caption font-semibold',
        effective === 'issued' && 'bg-warning/15 text-warning',
        effective === 'redeemed' && 'bg-success/15 text-success',
        effective === 'expired' && 'bg-light-gray text-slate-gray',
      )}
    >
      {REDEMPTION_STATUS_LABELS[effective]}
    </span>
  );
}

function BillEditor({
  redemption,
  onSave,
}: {
  redemption: AdminRedemption;
  onSave: (totalBill: number | null) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(redemption.totalBill ?? '');
  const [saving, setSaving] = useState(false);

  if (redemption.status !== 'redeemed') {
    // Bill capture only meaningful for redeemed perks — there's no purchase
    // event to attach the figure to otherwise.
    return <span className="text-caption text-slate-gray/60">—</span>;
  }

  if (!editing) {
    return (
      <button
        className="text-caption text-tech-blue hover:underline"
        onClick={() => {
          setDraft(redemption.totalBill ?? '');
          setEditing(true);
        }}
      >
        {redemption.totalBill ? formatBill(redemption.totalBill) : 'Add'}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        step="0.01"
        className="input w-24 px-2 py-1 text-right text-caption"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
      />
      <button
        className="btn-ghost px-2 py-1 text-caption"
        disabled={saving}
        onClick={async () => {
          setSaving(true);
          try {
            await onSave(draft === '' ? null : Number(draft));
            setEditing(false);
          } finally {
            setSaving(false);
          }
        }}
      >
        Save
      </button>
      <button
        className="btn-ghost px-2 py-1 text-caption"
        onClick={() => setEditing(false)}
      >
        ×
      </button>
    </div>
  );
}

function formatBill(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(2);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
